import {
  Stack,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Autocomplete,
  FormHelperText
} from '@mui/material';
import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Select from '@mui/material/Select';
import { FormikProps } from 'formik';
import { JoinFormType, PackagePrices } from 'types/join';
import 'intl-tel-input/build/css/intlTelInput.css';
import intlTelInput, { Iti, SomeOptions } from 'intl-tel-input';
import { useDispatch, useSelector } from 'react-redux';
import { states, getStateArrayByCountry, countries } from 'constants/locations';
import { CountryCodeMap, CountryCodeArray } from './country-code';
import { PlanState } from '../reducers/plan';
import { backgrounds } from 'polished';
import { EmailErrorsTextsExists } from './email-error-text';
import { errorList, errorListTypes } from 'constants/error-list';
import { resetError } from '../actions';

const ErrorIcon = styled('div')(({ theme }) => ({
  width: '16px',
  height: '16px',
  backgroundColor: (theme.palette as any).red,
  color: 'white',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 'bold',
  position: 'absolute',
  top: '-8px',
  right: '-8px',
  zIndex: 1
}));

const SelectWrapper = styled('div')({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  width: '100%' // Ensure it matches the select width
});

const MainContainer = styled(Stack)(({ theme }) => ({
  width: '655px',
  paddingTop: '30px',
  paddingBottom: '35px',
  backgroundColor: (theme.palette as any).siteGrey,
  '&:hover': {
    cursor: 'default' // Ensure cursor remains default on hover
  }
}));
const TField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    border: `1px solid ${(theme.palette as any).hoveredTag}`,
    backgroundColor: 'white'
  },
  '& .MuiFormHelperText-root': {
    fontSize: '12px'
  },
  '& input.MuiInputBase-input': {
    border: 'none',
    color: (theme.palette as any).siteBlack,
    fontSize: '12px',
    // required to reset the styles.css wrong padding across the app
    padding: '9px 14px'
  },
  '& .Mui-disabled': {
    backgroundColor: (theme.palette as any).siteGrey,
    '&:hover': {
      cursor: 'default'
    }
  },
  '& .iti__flag, & .iti__selected-flag, & .iti__selected-dial-code, & .iti__country-list, & .iti__selected-country':
    {
      cursor: 'default',
      '&:hover': {
        cursor: 'default'
      }
    }
}));
const TSelect = styled(Select)(({ theme }) => ({
  backgroundColor: 'white',
  border: `1px solid ${(theme.palette as any).hoveredTag}`,
  '& .MuiSvgIcon-root': {
    color: (theme.palette as any).siteBlue,
    opacity: 0.4,
    '&:hover': {
      cursor: 'default'
    }
  },
  '& .Mui-disabled': {
    backgroundColor: (theme.palette as any).siteGrey,
    '&:hover': {
      cursor: 'default'
    }
  }
}));
const Fieldset = styled('fieldset')(({ disabled }) => ({
  border: 'none',
  padding: 0,
  opacity: disabled ? 0.5 : 1
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme, disabled }) => ({
  backgroundColor: disabled ? (theme.palette as any).siteGrey : (theme.palette as any).white,
  '& .MuiIconButton-root': {
    color: (theme.palette as any).siteBlue,
    opacity: 0.4,
    marginTop: '4px',
    '&:hover': {
      cursor: 'default'
    }
  }
}));

const FieldLabel = ({
  text,
  required,
  error,
  children
}: {
  text: string;
  required?: boolean;
  children: React.ReactNode;
  error?: boolean;
}) => {
  return (
    <Stack>
      <Stack direction="row" gap={0.5}>
        <Typography variant="body1" sx={{ fontFamily: 'Montserrat-SemiBold', fontSize: '11px' }}>
          {text}
        </Typography>
        {required && (
          <Typography variant="body1" color="red" sx={{ height: '4px' }}>
            *
          </Typography>
        )}
        {error && <ErrorIcon>!</ErrorIcon>}
      </Stack>
      {children}
    </Stack>
  );
};

export const Details = ({
  form,
  disabled = false,
  isPriceList = false,
  selectedPlan
}: {
  form: FormikProps<JoinFormType>;
  disabled: boolean;
  isPriceList: boolean;
  selectedPlan: PackagePrices | undefined;
}) => {
  const [mobile, setMobile] = useState<Iti>();
  const [office, setOffice] = useState<Iti>();
  const { companyTypes, showErrorPopup, subscriptionId } =
    useSelector((state: { plan: PlanState }) => state.plan) || {};
  const errorMessageType = errorList[showErrorPopup.errorMessage || ''] || '';
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      errorMessageType === errorListTypes.emailAlreadyExists ||
      errorMessageType === errorListTypes.emailIsNotValid
    ) {
      dispatch(resetError());
    }
  }, [form.values.email]);

  useEffect(() => {
    if (
      Object.keys(form.errors).length > 0 ||
      (showErrorPopup.show &&
        (errorMessageType === errorListTypes.emailAlreadyExists ||
          errorMessageType === errorListTypes.emailIsNotValid))
    ) {
      const formContainer = document.getElementById('details-section-id');
      if (formContainer) {
        const elementPosition = formContainer.getBoundingClientRect().top + window.scrollY;
        const offset = 50;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth'
        });
      }
    }
  }, [showErrorPopup, form.isSubmitting]);

  useEffect(() => {
    if (getStateArrayByCountry(form.values.companyCountry ?? '').length === 0) {
      form.setFieldValue('accountState', '');
    }
  }, [form.values.companyCountry]);

  useEffect(() => {
    const officeInput = document.querySelector('#office-number') as HTMLInputElement;
    const mobileInput = document.querySelector('#mobile-number') as HTMLInputElement;
    if (
      form.values.companyCountry &&
      form.values.companyCountry !== null &&
      form.values.companyCountry !== ''
    ) {
      const countryCode =
        CountryCodeArray.find(
          (country: { country: string; countryCode: string; phonePrefix: string }) =>
            country.country === form.values.companyCountry
        )?.countryCode ||
        CountryCodeMap[form.values.companyCountry as keyof typeof CountryCodeMap]?.countryCode ||
        'auto';
      if (officeInput) {
        if (office) {
          office.destroy();
          officeInput.value = '';
          form.setFieldValue('phone', null);
        }
        const phone = intlTelInput(
          officeInput as HTMLInputElement,
          {
            initialCountry: countryCode,
            separateDialCode: true,
            utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
            allowDropdown: false,
            showSelectedDialCode: true,
            showFlags: true,
            onlyCountries: [countryCode]
          } as SomeOptions
        );

        setOffice(phone);
        const selectedDialCodeElement =
          (officeInput && officeInput.closest('.iti')?.querySelector('.iti__selected-dial-code')) ||
          (mobileInput && mobileInput.closest('.iti')?.querySelector('.iti__selected-dial-code'));

        if (selectedDialCodeElement) {
          selectedDialCodeElement.classList.add('no-cursor-pointer');
        }
      }
      if (mobileInput) {
        if (mobile) {
          mobile.destroy();
          mobileInput.value = '';
          form.setFieldValue('cellPhone', null);
        }
        const phone = intlTelInput(
          mobileInput as HTMLInputElement,
          {
            initialCountry: countryCode,
            separateDialCode: true,
            utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
            allowDropdown: false,
            showSelectedDialCode: true,
            showFlags: true,
            onlyCountries: [countryCode]
          } as SomeOptions
        );
        setMobile(phone);
      }
    }
  }, [form.values.companyCountry, isPriceList]);

  const changeOfficePhone = () => {
    const officeNumber = office?.getNumber();
    form.setFieldValue('phone', officeNumber);
  };
  const changeMobilePhone = () => {
    const mobileNumber = mobile?.getNumber();
    form.setFieldValue('cellPhone', mobileNumber);
  };
  const getPhoneValue = (isMobile = false) => {
    let phone = form.values.phone;
    let countryCode = office?.getSelectedCountryData()?.dialCode;
    if (isMobile) {
      phone = form.values.cellPhone;
      countryCode = mobile?.getSelectedCountryData()?.dialCode;
    }
    if (countryCode && phone) {
      if (phone.startsWith(`+${countryCode}`)) {
        phone = phone.slice(countryCode.length + 1);
        phone = phone.replace(/\D/g, '');
      } else {
        form.setFieldValue(isMobile ? 'cellPhone' : 'phone', `+${countryCode}${phone}`);
      }
    }
    return phone;
  };

  const rapnetFields = [
    'firstName',
    'lastName',
    'email',
    'companyName',
    'companyType',
    'companyCountry',
    'address',
    'city',
    'state',
    'accountState',
    'zipCode',
    'phone',
    'cellPhone'
  ];

  const priceListFields = [
    'firstName',
    'lastName',
    'email',
    'companyCountry',
    'companyName',
    'companyType',
    'phone',
    ...(form.values.hasMagazineAddon ? ['address', 'city', 'state', 'zipCode'] : [])
  ];

  const componentsList: Array<{ name: string; component: any }> = [
    {
      name: 'firstName',
      component: (
        <Grid item xs={!isPriceList ? 4 : 6}>
          <Stack>
            <FieldLabel text="First Name" required>
              <TField
                placeholder="First Name"
                fullWidth
                name="firstName"
                value={form.values.firstName}
                onChange={form.handleChange}
                InputProps={{
                  sx: {
                    fontFamily: 'Montserrat-Light',
                    fontSize: '12px'
                  },
                  endAdornment:
                    form.touched.firstName && form.errors.firstName ? (
                      <ErrorIcon>!</ErrorIcon>
                    ) : null
                }}
                error={form.touched.firstName && Boolean(form.errors.firstName)}
                helperText={
                  form.touched.firstName &&
                  form.errors.firstName &&
                  form.errors.firstName !== 'Required'
                    ? form.errors.firstName
                    : null
                }
              />
            </FieldLabel>
          </Stack>
        </Grid>
      )
    },
    {
      name: 'lastName',
      component: (
        <Grid item xs={!isPriceList ? 4 : 6}>
          <FieldLabel text="Last Name" required>
            <TField
              placeholder="Last Name"
              fullWidth
              name="lastName"
              value={form.values.lastName}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Montserrat-Light',
                  fontSize: '12px'
                },
                endAdornment:
                  form.touched.lastName && form.errors.lastName ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={form.touched.lastName && Boolean(form.errors.lastName)}
              helperText={
                form.touched.lastName && form.errors.lastName && form.errors.lastName !== 'Required'
                  ? form.errors.lastName
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'email',
      component: (
        <Grid item xs={!isPriceList ? 4 : 6}>
          <FieldLabel text="Email" required>
            <TField
              placeholder="Email"
              disabled={form.values.fromCache || subscriptionId !== null}
              fullWidth
              name="email"
              value={form.values.email}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Montserrat-Light',
                  fontSize: '12px'
                },
                endAdornment:
                  form.touched.email && form.errors.email ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={
                form.touched.email &&
                (Boolean(form.errors.email) ||
                  (!isPriceList && errorMessageType === errorListTypes.emailAlreadyExists) ||
                  errorMessageType === errorListTypes.emailIsNotValid)
              }
              helperText={
                <EmailErrorsTextsExists
                  showErrorPopup={showErrorPopup}
                  isPriceList={isPriceList}
                  form={form}
                />
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'companyName',
      component: (
        <Grid item xs={4}>
          <FieldLabel text="Company Name" required={!isPriceList}>
            <TField
              placeholder="Company Name"
              fullWidth
              name="companyName"
              value={form.values.companyName}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Montserrat-Light',
                  fontSize: '12px'
                },
                endAdornment:
                  form.touched.companyName && form.errors.companyName ? (
                    <ErrorIcon>!</ErrorIcon>
                  ) : null
              }}
              error={form.touched.companyName && Boolean(form.errors.companyName)}
              helperText={
                form.touched.companyName &&
                form.errors.companyName &&
                form.errors.companyName !== 'Required'
                  ? form.errors.companyName
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'companyType',
      component: (
        <Grid item xs={4}>
          <FieldLabel text="Company Type" required={!isPriceList}>
            <SelectWrapper>
              <TSelect
                MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                placeholder="Company Type"
                size="small"
                name="companyType"
                disabled={disabled}
                value={form.values.companyType}
                onChange={form.handleChange}
                error={form.touched.companyType && Boolean(form.errors.companyType)}
                style={{ fontSize: '12px', fontFamily: 'Montserrat-Light', width: '210px' }}
              >
                {companyTypes
                  .map(type => type.name)
                  .sort()
                  .map((companyType: string) => (
                    <MenuItem key={companyType} value={companyType}>
                      {companyType}
                    </MenuItem>
                  ))}
              </TSelect>
              {form.touched.companyType && Boolean(form.errors.companyType) ? (
                <ErrorIcon>!</ErrorIcon>
              ) : null}
            </SelectWrapper>
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'companyCountry',
      component: (
        <Grid item xs={!isPriceList ? 4 : 6}>
          <FieldLabel text="Country" required>
            <StyledAutocomplete
              options={countries.sort()}
              value={form.values.companyCountry || null}
              onChange={(event, newValue) => {
                form.setFieldValue('companyCountry', newValue);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Country"
                  size="small"
                  name="companyCountry"
                  error={form.touched.companyCountry && Boolean(form.errors.companyCountry)}
                  helperText={
                    form.touched.companyCountry &&
                    form.errors.companyCountry &&
                    form.errors.companyCountry !== 'Required'
                      ? form.errors.companyCountry
                      : null
                  }
                  InputProps={{
                    ...params.InputProps,
                    style: { fontSize: '12px', fontFamily: 'Montserrat-Light' },
                    endAdornment:
                      form.touched.companyCountry && form.errors.companyCountry ? (
                        <ErrorIcon>!</ErrorIcon>
                      ) : null
                  }}
                />
              )}
              disabled={
                form.values.fromCache ||
                subscriptionId !== null ||
                selectedPlan?.packageName.toLowerCase().includes('india')
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'address',
      component: (
        <Grid item xs={4}>
          <FieldLabel text="Address" required>
            <TField
              placeholder="Address"
              fullWidth
              name="address"
              value={form.values.address}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Montserrat-Light',
                  fontSize: '12px'
                },
                endAdornment:
                  form.touched.address && form.errors.address ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={form.touched.address && Boolean(form.errors.address)}
              helperText={
                form.touched.address && form.errors.address && form.errors.address !== 'Required'
                  ? form.errors.address
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'city',
      component: (
        <Grid item xs={3}>
          <FieldLabel text="City" required>
            <TField
              placeholder="City"
              fullWidth
              name="city"
              value={form.values.city}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Montserrat-Light',
                  fontSize: '12px'
                },
                endAdornment:
                  form.touched.city && form.errors.city ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={form.touched.city && Boolean(form.errors.city)}
              helperText={
                form.touched.city && form.errors.city && form.errors.city !== 'Required'
                  ? form.errors.city
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'state',
      component: (
        <Grid item xs={3}>
          <FieldLabel text="State">
            {getStateArrayByCountry(form.values.companyCountry ?? '').length === 0 ? (
              <TField
                disabled
                placeholder="State"
                size="small"
                name="accountState"
                value={form.values.accountState}
                onChange={form.handleChange}
                error={form.touched.accountState && Boolean(form.errors.accountState)}
                style={{ fontSize: '12px', fontFamily: 'Montserrat-Light' }}
              />
            ) : (
              <TSelect
                MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                placeholder="State"
                size="small"
                name="accountState"
                value={form.values.accountState}
                onChange={form.handleChange}
                error={form.touched.accountState && Boolean(form.errors.accountState)}
                style={{ fontSize: '12px', fontFamily: 'Montserrat-Light' }}
              >
                {getStateArrayByCountry(form.values.companyCountry ?? '')
                  .sort()
                  .map((state: string) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
              </TSelect>
            )}
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'zipCode',
      component: (
        <Grid item xs={2}>
          <FieldLabel text="Zip Code" required>
            <TField
              placeholder="Zip Code"
              fullWidth
              name="zipCode"
              value={form.values.zipCode}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Montserrat-Light',
                  fontSize: '12px'
                },
                endAdornment:
                  form.touched.zipCode && form.errors.zipCode ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={form.touched.zipCode && Boolean(form.errors.zipCode)}
              helperText={
                form.touched.zipCode && form.errors.zipCode && form.errors.zipCode !== 'Required'
                  ? form.errors.zipCode
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'phone',
      component: (
        <Grid id="grid-office-number" item xs={!isPriceList ? 6 : 4}>
          <FieldLabel
            text={!isPriceList ? 'Office Number' : 'Phone Number'}
            required={!isPriceList}
          >
            <TField
              id="office-number"
              placeholder="Office Number"
              type="tel"
              fullWidth
              name="phone"
              value={getPhoneValue()}
              onChange={changeOfficePhone}
              InputProps={{
                endAdornment:
                  form.touched.phone && form.errors.phone ? <ErrorIcon>!</ErrorIcon> : null
              }}
              sx={{ fontFamily: 'Montserrat-Light', fontSize: '12px' }}
              error={form.touched.phone && Boolean(form.errors.phone)}
              helperText={
                form.touched.phone && form.errors.phone && form.errors.phone !== 'Required'
                  ? form.errors.phone
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'cellPhone',
      component: (
        <Grid item xs={6}>
          <FieldLabel text="Mobile Number">
            <TField
              id="mobile-number"
              placeholder="Mobile Number"
              type="tel"
              fullWidth
              name="cellPhone"
              value={getPhoneValue(true)}
              onChange={changeMobilePhone}
              InputProps={{
                sx: {
                  fontFamily: 'Montserrat-Light',
                  fontSize: '12px'
                }
              }}
            />
          </FieldLabel>
        </Grid>
      )
    }
  ];

  return (
    <MainContainer gap={2} id="details-section-id">
      <Fieldset disabled={disabled}>
        <Grid container spacing={2} rowSpacing={2}>
          {isPriceList &&
            priceListFields.map(field => componentsList.find(c => c.name === field)?.component)}
          {!isPriceList &&
            rapnetFields.map(field => componentsList.find(c => c.name === field)?.component)}
        </Grid>
      </Fieldset>
    </MainContainer>
  );
};
export default Details;
